import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import { OrdersPage } from '@/pages/orders';
import { requireAuth } from '../require-auth';

/**
 * URL state for the Orders screen. The URL is the single source of truth
 * (CLAUDE.md state-boundary table): `search` (free text), `from`/`to`
 * (`YYYY-MM-DD` date-range) scope the list, `allBranches=false` narrows it to
 * the active branch, and `status` pins the mobile screen's chip rail; `orderId`
 * opens the detail drawer. All optional — an unfiltered page has a bare URL.
 *
 * «Все филиалы» is a URL param rather than a value in the shared branch scope
 * (`ui-store.activeBranchId`) because that scope is also what the Menu and
 * Promotions screens read, and both are strictly branch-scoped — their
 * endpoints take a `restaurant_id` with no merchant-wide variant. A null branch
 * there would have nothing to fall back to. Keeping it local to this route also
 * means the picked branch survives switching back off «Все».
 */
const ordersSearchSchema = z.object({
  orderId: z.string().optional(),
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().positive().catch(1).optional(),
  limit: z.number().int().positive().catch(15).optional(),
  // «Все филиалы» is the default orders view. Only `false` needs to be present,
  // so a bare URL opens with the branch filter on "All".
  allBranches: z.boolean().optional(),
  // The mobile status chip rail. `.catch` rather than a bare enum so a stale
  // or hand-edited value degrades to the unfiltered view instead of failing
  // the whole route's search validation.
  status: z
    .enum(['all', 'new', 'preparing', 'ready', 'picked_up', 'delivered', 'cancelled'])
    .catch('all')
    .optional(),
});

// No loader prefetch: the list is keyed by the active branch AND the URL
// filters, neither of which is known here — the branch resolves only after the
// branch list loads. The page's own query owns the fetch.
export const Route = createFileRoute('/eats/orders')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: ordersSearchSchema,
  component: OrdersRoute,
});

function OrdersRoute() {
  const { orderId, search, from, to, status, allBranches, page, limit } =
    Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <OrdersPage
      selectedId={orderId}
      search={search}
      from={from}
      to={to}
      status={status}
      page={page ?? 1}
      limit={limit ?? 15}
      allBranches={allBranches ?? true}
      onSelectOrder={(id) => {
        void navigate({ search: (prev) => ({ ...prev, orderId: id }) });
      }}
      onSelectAllBranches={(next) => {
        // All is the default, so it clears the param; choosing a branch writes
        // `allBranches=false` because the absence of the param means "All".
        void navigate({
          search: (prev) => ({
            ...prev,
            allBranches: next ? undefined : false,
            page: undefined,
          }),
        });
      }}
      onSelectStatus={(next) => {
        // `all` is the default view, so it clears the param rather than
        // spelling itself out in the URL.
        void navigate({
          search: (prev) => ({
            ...prev,
            status: next === 'all' ? undefined : next,
            page: undefined,
          }),
        });
      }}
      onPageChange={(nextPage) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            page: nextPage === 1 ? undefined : nextPage,
          }),
        });
      }}
      onLimitChange={(nextLimit) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            limit: nextLimit === 15 ? undefined : nextLimit,
            page: undefined,
          }),
        });
      }}
    />
  );
}
